/**
 * CrewListContent Component
 * Main crew list view with search, filtering, and actions
 *
 * Architecture: Lifted State Pattern (FIX: 2025-02-12)
 * - Receives crew data as prop from CrewModule (single source of truth)
 * - Still calls useCrew() for mutation functions (updateCrew, deleteCrew, etc.)
 * - Fixes display bug where new crew didn't appear until navigation refresh
 *
 * UI Pattern: 2/3 + 1/3 layout (matches SafetyDrillsTab)
 * - Desktop: Main content (2/3) + Sidebar (1/3)
 * - Mobile: Stacked content + MobileBottomBar + Summary box
 */

import React, { useState, useMemo } from 'react';
import { Download, PlusCircle, Search, Settings } from 'lucide-react';
import { useStorageState } from '../../../../hooks/useStorageState';
import { useHoursOfRestData } from '../../../../hooks/domain/useHoursOfRestData';
import { useVessel } from 'contexts/VesselContext';
import { useCrew } from '../../../../hooks/useCrew';
import { useSyncStatus } from '../../../../hooks/useSyncStatus';
import FilterTabs from '../../common/FilterTabs';
import CrewFilters from './components/CrewFilters';
import CrewBulkActions from './components/CrewBulkActions';
import CrewTable from './components/CrewTable';
import CrewMobileCards from './components/CrewMobileCards';
import CrewSidebar from './components/CrewSidebar';
import CrewSummaryCard from './components/CrewSummaryCard';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import {
  filterCrewMembers,
  calculateCrewStats,
  exportCrewToCSV
} from './utils/crewHelpers';

const CrewListContent = ({
  crew,  // FIX: Receive crew from parent (CrewModule) to ensure same React state instance
  handleQuickHOR,
  onAddCrew,
  onAddFromOrg,  // FT-05: Add from organisation roster
  onViewCrew,
  onEditCrew,
  onOpenSettings,
  isFleetView = false,
  onToggleFleetView
}) => {
  // FIX: Use crew prop from parent for display (single source of truth)
  // Keep useCrew() for mutation functions only
  const { updateCrew, deleteCrew, toggleCrewDutyStatus, isOnDuty, loading } = useCrew({ includeInactive: true });
  const crewMembers = crew || [];
  const { isSyncing } = useSyncStatus();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCrew, setSelectedCrew] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [activeFilterTab, setActiveFilterTab] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'full_name', direction: 'asc' });
  // FT-164: Use new flat array hook
  const { data: horRecords = [] } = useHoursOfRestData();
  // FT-164: Convert flat array to legacy nested lookup for compatibility
  const hoursData = useMemo(() => {
    const nested = {};
    for (const record of horRecords) {
      if (!record.crewId || !record.date) continue;
      if (!nested[record.crewId]) nested[record.crewId] = {};
      nested[record.crewId][record.date] = record;
    }
    return nested;
  }, [horRecords]);
  const { value: familiarisationRecords = [] } = useStorageState('familiarisation_records', []);
  const { value: familiarisationSettings = {} } = useStorageState('familiarisationSettings', {});
  const familiarisationValidityMonths = familiarisationSettings.validityPeriodMonths || 12;
  const { currentVesselId, getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const drillsKey = currentVesselId ? `drills_${currentVesselId}` : 'drills';
  const { value: drillsData = [] } = useStorageState(drillsKey, []);

  // Calculate crew statistics
  const crewStats = useMemo(() => {
    return calculateCrewStats(crewMembers, hoursData, currentVessel, drillsData, familiarisationRecords, familiarisationValidityMonths, currentVesselId);
  }, [crewMembers, hoursData, currentVessel, drillsData, familiarisationRecords, familiarisationValidityMonths]);

  // FilterTabs configuration with counts - renamed "Active" to "On Duty" per Advisor spec
  const filterTabs = useMemo(() => [
    { id: 'all', label: 'All', count: crewStats.total },
    { id: 'onduty', label: 'On Duty', count: crewStats.active },  // Renamed from 'active' to 'onduty'
    { id: 'offduty', label: 'Off Duty', count: crewStats.offDuty },
    { id: 'issues', label: 'Issues', count: crewStats.issues + crewStats.warnings }
  ], [crewStats]);

  // Filter crew members based on tab + search
  const filteredCrew = useMemo(() => {
    // First apply tab filter - now uses isOnDuty for backwards compatibility
    let tabFiltered = crewMembers;
    if (activeFilterTab === 'onduty') {
      // On Duty: includes both 'active' and 'on duty' statuses
      tabFiltered = crewMembers.filter(m => isOnDuty(m));
    } else if (activeFilterTab === 'offduty') {
      // Off Duty: anyone NOT on duty and NOT archived
      tabFiltered = crewMembers.filter(m => !isOnDuty(m) && m.status !== 'archived');
    } else if (activeFilterTab === 'issues') {
      // Import getCrewCompliance to check issues
      const { getCrewCompliance } = require('./utils/crewHelpers');
      tabFiltered = crewMembers.filter(m => {
        const compliance = getCrewCompliance(m, hoursData, currentVessel, drillsData, familiarisationRecords, familiarisationValidityMonths);
        return compliance.type === 'error' || compliance.type === 'warning';
      });
    }

    // Then apply existing filters (search, status, compliance)
    return filterCrewMembers(tabFiltered, {
      searchTerm,
      statusFilter,
      complianceFilter,
      hoursData,
      vesselConfig: currentVessel,
      drillsData,
      familiarisationRecords,
      familiarisationValidityMonths
    });
  }, [crewMembers, activeFilterTab, searchTerm, statusFilter, complianceFilter, hoursData, currentVessel, drillsData, familiarisationRecords, familiarisationValidityMonths, isOnDuty]);

  // Helper: Get earliest certificate expiry date
  const getEarliestExpiry = (certificates) => {
    if (!certificates || certificates.length === 0) return null;
    const dates = certificates
      .map(cert => cert.expiry_date || cert.expiry)
      .filter(date => date);
    if (dates.length === 0) return null;
    return dates.sort()[0]; // Earliest date
  };

  // Sort filtered crew based on sortConfig
  const sortedCrew = useMemo(() => {
    if (!sortConfig.key || sortConfig.direction === null) {
      return filteredCrew; // Return unsorted
    }

    return [...filteredCrew].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle certificate expiry sorting (find earliest expiring cert)
      if (sortConfig.key === 'certificateExpiry') {
        const aExpiry = getEarliestExpiry(a.certificates);
        const bExpiry = getEarliestExpiry(b.certificates);
        aVal = aExpiry ? new Date(aExpiry) : new Date('2099-12-31');
        bVal = bExpiry ? new Date(bExpiry) : new Date('2099-12-31');
      }

      // Handle date sorting (start_date)
      if (sortConfig.key === 'start_date') {
        aVal = aVal ? new Date(aVal) : new Date('2099-12-31');
        bVal = bVal ? new Date(bVal) : new Date('2099-12-31');
      }

      // Handle status sorting (normalize case)
      if (sortConfig.key === 'status') {
        aVal = (aVal || 'Active').toLowerCase();
        bVal = (bVal || 'Active').toLowerCase();
      }

      // String comparison (case-insensitive)
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCrew, sortConfig]);

  // Handle sort column click - three-state: asc → desc → null
  const handleSort = (key) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        if (prevConfig.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return { key: null, direction: null }; // Reset to unsorted
        }
      }
      return { key, direction: 'asc' }; // New column, start with asc
    });
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCrew(filteredCrew.map(m => m.id));
    } else {
      setSelectedCrew([]);
    }
  };
  
  // Handle individual selection
  const handleSelectCrew = (id) => {
    setSelectedCrew(prev => 
      prev.includes(id) 
        ? prev.filter(cId => cId !== id)
        : [...prev, id]
    );
  };

  // Toggle bulk actions
  const handleToggleBulkActions = () => {
    setShowBulkActions(!showBulkActions);
    setSelectedCrew([]);
  };

  // Bulk action handlers - now use useCrew hook operations
  const handleBulkSignOn = (selectedIds) => {
    selectedIds.forEach(id => {
      updateCrew(id, { status: 'Active' });
    });
    setSelectedCrew([]);
  };

  const handleBulkSignOff = (selectedIds) => {
    selectedIds.forEach(id => {
      updateCrew(id, { status: 'Off Duty' });
    });
    setSelectedCrew([]);
  };

  const handleBulkDelete = (selectedIds) => {
    selectedIds.forEach(id => {
      deleteCrew(id);
    });
    setSelectedCrew([]);
  };

  // Quick action handlers for individual crew members - now use useCrew hook
  const handleQuickSignOn = (member) => {
    updateCrew(member.id, { status: 'Active' });
  };

  const handleQuickSignOff = (member) => {
    updateCrew(member.id, { status: 'Off Duty' });
  };

  // Toggle duty status - uses new unified duty status approach
  const handleToggleDutyStatus = (member) => {
    toggleCrewDutyStatus(member.id);
  };

  // Export crew list
  const exportCrewList = () => {
    exportCrewToCSV(filteredCrew, 'crew-list');
  };

  // FT-358: register middle-slot bottom-bar actions per brief §6
  // (Crew list: [+ Add] [Export]).
  useSetBottomBarActions([
    {
      key: 'crew-add',
      icon: PlusCircle,
      label: 'Add',
      primary: true,
      onClick: onAddCrew,
    },
    {
      key: 'crew-export',
      icon: Download,
      label: 'Export',
      onClick: exportCrewList,
    },
  ]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Main Content - 2/3 width on desktop */}
      <div className="lg:col-span-2">
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          {/* Header - slate-600 with search inside */}
          <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-base font-semibold text-white whitespace-nowrap">
                Crew List
              </h3>

              {/* Search Input */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search crew..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-white/20 rounded-md bg-white text-slate-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {/* Desktop-only fleet view toggle */}
              {onToggleFleetView && (
                <button
                  onClick={onToggleFleetView}
                  className="hidden lg:inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-white/10 border border-white/20 rounded-md hover:bg-white/20"
                >
                  {isFleetView ? 'Vessel View' : 'Fleet View'}
                </button>
              )}

              {/* Settings gear icon */}
              {onOpenSettings && (
                <button
                  onClick={onOpenSettings}
                  className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  title="Crew Settings"
                >
                  <Settings className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* FilterTabs - below header */}
          <div className="px-6 pt-4">
            <FilterTabs
              tabs={filterTabs}
              activeTab={activeFilterTab}
              onTabChange={setActiveFilterTab}
            />
          </div>

          {/* Filters and Bulk Actions - Status dropdown removed (handled by FilterTabs) */}
          <div className="px-6 py-4">
            <CrewFilters
              complianceFilter={complianceFilter}
              onComplianceFilterChange={setComplianceFilter}
              showBulkActions={showBulkActions}
              onToggleBulkActions={handleToggleBulkActions}
              selectedCount={selectedCrew.length}
            />
          </div>

          {/* Bulk Actions Menu */}
          <CrewBulkActions
            selectedCrew={selectedCrew}
            crewMembers={crewMembers}
            onSignOn={handleBulkSignOn}
            onSignOff={handleBulkSignOff}
            onDelete={handleBulkDelete}
            onClearSelection={() => setSelectedCrew([])}
          />

          {/* Content - add bottom padding for mobile bottom bar */}
          <div className="p-6 pb-24 lg:pb-6">
            {/* Desktop table view */}
            <CrewTable
              filteredCrew={sortedCrew}
              selectedCrew={selectedCrew}
              showBulkActions={showBulkActions}
              hoursData={hoursData}
              vesselConfig={currentVessel}
              drillsData={drillsData}
              familiarisationRecords={familiarisationRecords}
              familiarisationValidityMonths={familiarisationValidityMonths}
              onSelectAll={handleSelectAll}
              onSelectCrew={handleSelectCrew}
              onViewCrew={onViewCrew}
              onEditCrew={onEditCrew}
              onAddCrew={onAddCrew}
              onQuickHOR={handleQuickHOR}
              onQuickSignOn={handleQuickSignOn}
              onQuickSignOff={handleQuickSignOff}
              onToggleDutyStatus={handleToggleDutyStatus}
              isOnDuty={isOnDuty}
              searchTerm={searchTerm}
              sortConfig={sortConfig}
              onSort={handleSort}
              isSyncing={isSyncing}
            />

            {/* Mobile card view */}
            <CrewMobileCards
              filteredCrew={sortedCrew}
              hoursData={hoursData}
              vesselConfig={currentVessel}
              drillsData={drillsData}
              familiarisationRecords={familiarisationRecords}
              familiarisationValidityMonths={familiarisationValidityMonths}
              onViewCrew={onViewCrew}
              onEditCrew={onEditCrew}
              onAddCrew={onAddCrew}
              onQuickHOR={handleQuickHOR}
              onQuickSignOn={handleQuickSignOn}
              onQuickSignOff={handleQuickSignOff}
              onToggleDutyStatus={handleToggleDutyStatus}
              isOnDuty={isOnDuty}
              isSyncing={isSyncing}
              searchTerm={searchTerm}
            />
          </div>
        </div>
      </div>

      {/* Sidebar - 1/3 width - Hidden on mobile */}
      <div className="hidden lg:block">
        <CrewSidebar
          crewStats={crewStats}
          onAddCrew={onAddCrew}
          onAddFromOrg={onAddFromOrg}  // FT-05: Add from organisation
          onExport={exportCrewList}
          onOpenSettings={onOpenSettings}
        />
      </div>

      {/* Mobile Summary Box - ONLY visible on mobile (<lg) */}
      <div className="lg:hidden mb-20 px-4">
        <CrewSummaryCard crewStats={crewStats} />
      </div>

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. */}
    </div>
  );
};

export default CrewListContent;